import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, MessageSquare, Edit2, Archive } from 'lucide-react';
import api from '../../../utils/api';
import MainLayout from '../../../components/layout/MainLayout';
import { useAuth } from '../../../context/AuthContext';
import WWebTemplateFormModal from './components/WWebTemplateFormModal';

const WWebTemplateManager = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);

  const canEdit = ['super-admin', 'manager'].includes(user?.role);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api('/wa-web-templates');
      if (res.ok) {
        const data = await res.json();
        setTemplates(data);
      }
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Are you sure you want to archive this template?')) return;
    
    try {
      const res = await api(`/wa-web-templates/${id}`, { method: 'DELETE' });
      if (res.ok) {
        fetchTemplates();
      }
    } catch (err) {
      alert('Failed to archive template');
    }
  };

  return (
    <MainLayout>
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button 
              onClick={() => navigate('/automation')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '8px' }}
            >
              <ArrowLeft size={20} />
            </button>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>WhatsApp Web Templates</h1>
          </div>
          
          {canEdit && (
            <button 
              onClick={() => { setTemplateToEdit(null); setIsModalOpen(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}
            >
              <Plus size={18} /> New Template
            </button>
          )}
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Loading templates...</div>
        ) : templates.length === 0 ? (
          <div style={{ background: 'white', padding: '60px 20px', textAlign: 'center', borderRadius: '12px', border: '1px dashed var(--color-border-primary)' }}>
            <MessageSquare size={48} color="var(--color-text-tertiary)" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No Templates Found</h3>
            <p style={{ color: 'var(--color-text-secondary)', marginBottom: '24px' }}>Create your first WhatsApp Web template to use in bulk campaigns.</p>
            {canEdit && (
              <button 
                onClick={() => { setTemplateToEdit(null); setIsModalOpen(true); }}
                style={{ padding: '10px 20px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
              >
                Create Template
              </button>
            )}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
            {templates.map(template => (
              <div key={template._id} style={{ background: 'white', borderRadius: '12px', border: '1px solid var(--color-border-primary)', padding: '20px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>{template.name}</h3>
                    <span style={{ fontSize: '12px', padding: '2px 8px', background: 'var(--color-background-secondary)', borderRadius: '12px', color: 'var(--color-text-secondary)' }}>
                      {template.category}
                    </span>
                  </div>
                  
                  {canEdit && (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => { setTemplateToEdit(template); setIsModalOpen(true); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', padding: '4px' }}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button 
                        onClick={() => handleArchive(template._id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-error)', padding: '4px' }}
                      >
                        <Archive size={16} />
                      </button>
                    </div>
                  )}
                </div>
                
                <div style={{ background: 'var(--color-background-secondary)', padding: '12px', borderRadius: '8px', fontSize: '14px', color: 'var(--color-text-secondary)', flex: 1, whiteSpace: 'pre-wrap', marginBottom: '16px' }}>
                  {template.body}
                </div>
                
                <div style={{ borderTop: '1px solid var(--color-border-primary)', paddingTop: '16px', fontSize: '12px', color: 'var(--color-text-tertiary)', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Created by {template.createdByName}</span>
                  <span>Variables: {template.variables?.length || 0}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <WWebTemplateFormModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        templateToEdit={templateToEdit}
        onSave={fetchTemplates}
      />
    </MainLayout>
  );
};

export default WWebTemplateManager;
